import React, { FC, ReactNode } from 'react';

import EntitiesCacheProvider from './EntitiesCacheProvider';
import LinkedEntitiesProvider from './LinkedEntitiesProvider';
import PluginProvidersProvider from './PluginProvidersProvider';
import ReferencePickerPluginsProvider, {
  useReferencePickerPlugins,
  ReferencePickerPluginsContextProp,
} from './ReferencePickerPluginsProvider';
import RelationshipsProvider from './RelationshipsProvider';
import {
  Relationship,
  ResolvableCommonReferencePickerPlugin,
} from '../types/types';

interface ReferencePickerProviderProps {
  children: ReactNode | ReactNode[];
  sourceEntityIds?: string[];
  sourceEntityDomain: string;
  sourceEntityType: string;
  disableRelationshipService?: boolean;
  relationshipEntitiesList?: Relationship[];
}

interface ReferencePickerProviderWrapperProps extends ReferencePickerProviderProps {
  plugins: ResolvableCommonReferencePickerPlugin[];
  onPluginsLoadError?: ReferencePickerPluginsContextProp['onPluginsLoadError'];
}

const ReferencePickerProvider: FC<ReferencePickerProviderProps> = ({
  children,
  sourceEntityIds,
  sourceEntityDomain,
  sourceEntityType,
  disableRelationshipService,
  relationshipEntitiesList,
}) => {
  const { plugins } = useReferencePickerPlugins();

  return (
    <EntitiesCacheProvider>
      <RelationshipsProvider
        sourceEntityIds={sourceEntityIds}
        sourceEntityDomain={sourceEntityDomain}
        sourceEntityType={sourceEntityType}
        disableRelationshipService={disableRelationshipService}
        relationshipEntitiesList={relationshipEntitiesList}
      >
        <LinkedEntitiesProvider
          sourceEntityDomain={sourceEntityDomain}
          sourceEntityType={sourceEntityType}
          plugins={plugins}
        >
          <PluginProvidersProvider plugins={plugins}>
            {children}
          </PluginProvidersProvider>
        </LinkedEntitiesProvider>
      </RelationshipsProvider>
    </EntitiesCacheProvider>
  );
};

const ReferencePickerProviderWrapper: FC<
  ReferencePickerProviderWrapperProps
> = ({ plugins, onPluginsLoadError, ...rest }) => {
  return (
    <ReferencePickerPluginsProvider
      plugins={plugins}
      onPluginsLoadError={onPluginsLoadError}
    >
      <ReferencePickerProvider {...rest} />
    </ReferencePickerPluginsProvider>
  );
};

ReferencePickerProvider.displayName = 'ReferencePickerProvider';

export default ReferencePickerProviderWrapper;
